import { useFocusEffect } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { selectSelectedItem, setSelectedMachine } from '@reducers/machines';
import React, { useMemo } from 'react';
import { useHandleNavigationMachinesHeader } from './useHandleNavigationMachineHeader';
import { ALL_MACHINES, isPhone, navigationRef } from '@helpers';
import { ALL_MACHINES_OBJECT } from '@v2/pages/common/constants/base.constants';

export const useSelectMachine = (data: any[]) => {
  const dispatch = useDispatch();
  const selectedItem = useSelector(selectSelectedItem);
  const urlMachineId = navigationRef?.current?.getCurrentRoute()?.params?.id;

  const { shouldDisplayAllMachinesButton } = useHandleNavigationMachinesHeader();

  const newSelectedItem = useMemo(() => {
    if (!data.length) {
      return selectedItem || null;
    }

    const allMachinesSelectedAndAllowed = selectedItem?.id === ALL_MACHINES && shouldDisplayAllMachinesButton;

    if (selectedItem) {
      if (selectedItem?.id === ALL_MACHINES && shouldDisplayAllMachinesButton) {
        return ALL_MACHINES_OBJECT;
      }

      const machineBySelectedId = data.find((machine) => machine?.id === selectedItem.id);

      if (machineBySelectedId) {
        return machineBySelectedId;
      }
    }

    // Check URL parameter to determine intended machine selection
    const currentMachineId = urlMachineId ? Number(urlMachineId) : null;

    // Try to find machine matching URL parameter first
    if (currentMachineId) {
      const machineFromUrl = data.find((machine) => machine?.id === currentMachineId);
      if (machineFromUrl) {
        return machineFromUrl;
      }
    }

    // Only fallback to first machine if there's no URL parameter or no selected item or selectedItem is ALL_MACHINES but screen doesn't allowed to select that
    if (!currentMachineId && (!selectedItem || !allMachinesSelectedAndAllowed)) {
      const fallbackMachine = data[0] || null;
      return fallbackMachine;
    }

    // Wait for proper data loading
    return selectedItem;
  }, [data, selectedItem, shouldDisplayAllMachinesButton, urlMachineId]);

  useFocusEffect(
    React.useCallback(() => {
      if (!newSelectedItem || isPhone) return;

      // Prevent infinite loops by checking if the selection actually changed
      if (
        selectedItem?.id === newSelectedItem?.id &&
        selectedItem?.machineId === newSelectedItem?.machineId &&
        selectedItem?.hasNoDataInPeriod === newSelectedItem?.hasNoDataInPeriod
      ) {
        return;
      }
      dispatch(setSelectedMachine(newSelectedItem));
    }, [newSelectedItem, selectedItem, isPhone, dispatch])
  );
};
